import { OrderAttributionWrite } from '@repo/api/web/models'

import type { StoredAttribution } from '../models'
import { useAnalytics } from './useAnalytics'
import { useConsent } from './useConsent'

import { LocalStorageProvider } from '#Common/support'
import { attributionStorageKey } from '~/app/composables/cookie/key'

/**
 * Захват и хранение источника трафика (ТЗ аналитики §6.1–6.3).
 * Метки визита (UTM/yclid/referrer/landing) снимаются при первом заходе и не
 * перезаписываются (first-touch); ClientID Метрики берётся из провайдера в момент
 * оформления. Итоговый снапшот уходит в атрибуцию заказа — PII на бэкенде
 * дополнительно гейтится согласием
 */
export const useAttribution = () => {
  const store = new LocalStorageProvider()

  /**
   * Снять источник первого касания. Повторные заходы не перезаписывают снимок,
   * чтобы сохранить первоисточник привода пользователя
   */
  function captureFirstTouch() {
    if (typeof window === 'undefined') return
    if (store.get(attributionStorageKey)) return

    const params = new URLSearchParams(window.location.search)
    const get = (key: string) => params.get(key)?.trim() || undefined

    const snapshot: StoredAttribution = {
      yclid: get('yclid'),
      utmSource: get('utm_source'),
      utmMedium: get('utm_medium'),
      utmCampaign: get('utm_campaign'),
      utmContent: get('utm_content'),
      utmTerm: get('utm_term'),
      referrer: document.referrer || undefined,
      landingPage: window.location.pathname + window.location.search || undefined,
      capturedAt: new Date().toISOString(),
    }
    store.set(attributionStorageKey, snapshot)
  }

  /**
   * Собрать блок атрибуции для payload заказа: сохранённый источник + актуальный
   * ClientID + факт согласия на аналитику. Без согласия PII можно не собирать —
   * сервер всё равно отбросит её (consent-гейт в домене), но флаг передаём честно
   */
  async function buildOrderAttribution(): Promise<OrderAttributionWrite> {
    const { provider } = useAnalytics()
    const { analyticsAllowed } = useConsent()

    const consentAnalytics = analyticsAllowed()
    const clientId = consentAnalytics ? await provider.getClientId() : null
    const stored = (store.get(attributionStorageKey) as StoredAttribution | null) ?? undefined

    return OrderAttributionWrite.createLazy({
      consentAnalytics,
      clientId: clientId ?? undefined,
      yclid: stored?.yclid,
      utmSource: stored?.utmSource,
      utmMedium: stored?.utmMedium,
      utmCampaign: stored?.utmCampaign,
      utmContent: stored?.utmContent,
      utmTerm: stored?.utmTerm,
      referrer: stored?.referrer,
      landingPage: stored?.landingPage,
    })
  }

  return {
    captureFirstTouch,
    buildOrderAttribution,
  }
}
